import type { DailyMetrics, ProductData, ProductRecord } from './types'
import { deltaRate, metricsOn } from './utils'

export interface PeriodMetrics {
  gmv: number
  soldItemCount: number
  customerCount: number
  productVisitors: number
  cancelRefundAmount: number
  conversionRate: number
  averageOrderValue: number
}

export interface ProductContribution {
  product: ProductRecord
  current: PeriodMetrics
  previous: PeriodMetrics
  delta: number
  visitorRate: number | null
  conversionPoint: number
  averageOrderValueRate: number | null
  driver: string
  driverKey: 'traffic' | 'conversion' | 'aov'
}

export interface AnalysisResult {
  currentDates: string[]
  previousDates: string[]
  current: PeriodMetrics
  previous: PeriodMetrics
  gmvRate: number | null
  visitorRate: number | null
  conversionPoint: number
  averageOrderValueRate: number | null
  effects: Array<{ key: 'traffic' | 'conversion' | 'aov', label: string, value: number }>
  growth: ProductContribution[]
  decline: ProductContribution[]
  attention: Array<ProductContribution & { reason: 'decline' | 'conversion' | 'refund' }>
}

const emptyPeriod = (): PeriodMetrics => ({
  gmv: 0,
  soldItemCount: 0,
  customerCount: 0,
  productVisitors: 0,
  cancelRefundAmount: 0,
  conversionRate: 0,
  averageOrderValue: 0,
})

const finalizePeriod = (period: PeriodMetrics): PeriodMetrics => ({
  ...period,
  conversionRate: period.productVisitors ? period.customerCount / period.productVisitors * 100 : 0,
  averageOrderValue: period.customerCount ? period.gmv / period.customerCount : 0,
})

function sumMetrics(period: PeriodMetrics, metrics: DailyMetrics) {
  period.gmv += metrics.gmv
  period.soldItemCount += metrics.soldItemCount
  period.customerCount += metrics.customerCount
  period.productVisitors += metrics.productVisitors
  period.cancelRefundAmount += metrics.cancelRefundAmount
}

function aggregateProduct(product: ProductRecord, dates: string[]) {
  const period = emptyPeriod()
  dates.forEach((date) => sumMetrics(period, metricsOn(product, date)))
  return finalizePeriod(period)
}

function aggregateProducts(products: ProductRecord[], dates: string[]) {
  const period = emptyPeriod()
  products.forEach((product) => dates.forEach((date) => sumMetrics(period, metricsOn(product, date))))
  return finalizePeriod(period)
}

const signedRate = (value: number | null) => value === null ? '新增' : `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`

function contributionFor(product: ProductRecord, currentDates: string[], previousDates: string[]): ProductContribution {
  const current = aggregateProduct(product, currentDates)
  const previous = aggregateProduct(product, previousDates)
  const visitorRate = deltaRate(current.productVisitors, previous.productVisitors)
  const conversionPoint = current.conversionRate - previous.conversionRate
  const conversionRelative = previous.conversionRate
    ? conversionPoint / previous.conversionRate * 100
    : current.conversionRate > 0 ? 100 : 0
  const averageOrderValueRate = deltaRate(current.averageOrderValue, previous.averageOrderValue)
  const candidates = [
    { key: 'traffic' as const, score: Math.abs(visitorRate ?? (current.productVisitors ? 100 : 0)), label: `访客 ${signedRate(visitorRate)}` },
    { key: 'conversion' as const, score: Math.abs(conversionRelative), label: `转化 ${conversionPoint >= 0 ? '+' : ''}${conversionPoint.toFixed(2)}pct` },
    { key: 'aov' as const, score: Math.abs(averageOrderValueRate ?? (current.averageOrderValue ? 100 : 0)), label: `客单价 ${signedRate(averageOrderValueRate)}` },
  ]
  const main = candidates.sort((a, b) => b.score - a.score)[0]
  return {
    product,
    current,
    previous,
    delta: current.gmv - previous.gmv,
    visitorRate,
    conversionPoint,
    averageOrderValueRate,
    driver: main.label,
    driverKey: main.key,
  }
}

const median = (values: number[]) => {
  if (!values.length) return 0
  const sorted = values.slice().sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

export function buildAnalysis(data: ProductData, selectedDate: string): AnalysisResult {
  const selectedIndex = Math.max(0, data.meta.dates.indexOf(selectedDate))
  const currentDates = data.meta.dates.slice(Math.max(0, selectedIndex - 6), selectedIndex + 1)
  const previousDates = data.meta.dates.slice(Math.max(0, selectedIndex - 13), Math.max(0, selectedIndex - 6))
  const products = data.products.filter((product) => product.level === 'spu')
  const current = aggregateProducts(products, currentDates)
  const previous = aggregateProducts(products, previousDates)
  const contributions = products.map((product) => contributionFor(product, currentDates, previousDates))
  const growth = contributions.filter((item) => item.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 5)
  const decline = contributions.filter((item) => item.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 5)

  const trafficEffect = (current.productVisitors - previous.productVisitors)
    * (previous.conversionRate / 100) * previous.averageOrderValue
  const conversionEffect = current.productVisitors
    * ((current.conversionRate - previous.conversionRate) / 100) * previous.averageOrderValue
  const averageOrderValueEffect = current.productVisitors
    * (current.conversionRate / 100) * (current.averageOrderValue - previous.averageOrderValue)

  const active = contributions.filter((item) => item.current.productVisitors > 0)
  const medianVisitors = median(active.map((item) => item.current.productVisitors))
  const medianConversion = median(active.map((item) => item.current.conversionRate))
  const attention: AnalysisResult['attention'] = []
  const used = new Set<string>()
  const addAttention = (item: ProductContribution | undefined, reason: 'decline' | 'conversion' | 'refund') => {
    if (!item || used.has(item.product.id)) return
    used.add(item.product.id)
    attention.push({ ...item, reason })
  }
  addAttention(decline[0], 'decline')
  addAttention(
    active.filter((item) => item.current.productVisitors >= medianVisitors && item.current.conversionRate < medianConversion)
      .sort((a, b) => b.current.productVisitors - a.current.productVisitors)[0],
    'conversion',
  )
  addAttention(
    contributions.filter((item) => item.current.cancelRefundAmount > 0)
      .sort((a, b) => b.current.cancelRefundAmount - a.current.cancelRefundAmount)[0],
    'refund',
  )
  decline.forEach((item) => { if (attention.length < 3) addAttention(item, 'decline') })

  return {
    currentDates,
    previousDates,
    current,
    previous,
    gmvRate: deltaRate(current.gmv, previous.gmv),
    visitorRate: deltaRate(current.productVisitors, previous.productVisitors),
    conversionPoint: current.conversionRate - previous.conversionRate,
    averageOrderValueRate: deltaRate(current.averageOrderValue, previous.averageOrderValue),
    effects: [
      { key: 'traffic', label: '流量影响', value: trafficEffect },
      { key: 'conversion', label: '转化影响', value: conversionEffect },
      { key: 'aov', label: '客单价影响', value: averageOrderValueEffect },
    ],
    growth,
    decline,
    attention,
  }
}
